import {
  Box,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Collapse,
  Typography,
  Autocomplete,
  TextField,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { ListAlt } from "@mui/icons-material";
import React, { ChangeEvent, SyntheticEvent, useState } from "react";
import income from "../../income.json";
import assets from "../../assets.json";
import { useAppDispatch } from "../../redux/hooks";
import { proceed, setCountry as setCountryReducer } from "../../redux/QuestionnaireSlice";
import theme from "../../theme";
import { getScreenSize } from "../../utils/getScreenSize";
import { ICountry } from "../../interfaces";

interface QuestionnaireProps {
  countries: ICountry[];
  countriesLoading: boolean;
  countriesError: boolean;
}

export default function Questionnaire({ countries, countriesLoading, countriesError }: QuestionnaireProps) {
  const dispatch = useAppDispatch();

  const [isSurvivor, setIsSurvivor] = useState<boolean | null>(null);
  const [isValidDate, setIsValidDate] = useState<boolean | null>(null);
  const [isCompensated, setIsCompensated] = useState<boolean | null>(null);
  const [_, setCountry] = useState("");
  const [isEligible, setIsEligible] = useState<boolean | null>(null);
  const [incomeSum, setIncomeSum] = useState("");
  const [assetSum, setAssetSum] = useState("");
  const [isAboveAnnualIncome, setIsAboveAnnualIncome] = useState<
    boolean | null
  >(null);
  const [isAboveAssets, setIsAboveAssets] = useState<boolean | null>(null);

  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('lg'));
  
  const handleRadioChange = ({
    currentTarget,
  }: ChangeEvent<HTMLInputElement>) => {
    const value = currentTarget.value === "true" ? true : false;

    switch (currentTarget.name) {
      case "isSurvivor": {
        return setIsSurvivor(value);
      }

      case "isValidDate": {
        return setIsValidDate(value);
      }

      case "isCompensated": {
        return setIsCompensated(value);
      }

      case "annualIncome": {
        return setIsAboveAnnualIncome(value);
      }

      case "assets": {
        return setIsAboveAssets(value);
      }
    }
  };

  const handleInputChange = (
    _: SyntheticEvent,
    value: { name: string; eligible: boolean } | null
  ) => {
    setCountry(value ? value.name : "");

    setIsEligible(value ? value.eligible : null);

    if (value && !value.eligible) return;

    dispatch(setCountryReducer(value ? value : ""));

    if (value) {
      const maxIncome = income.find((_income) => _income.name === value.name);

      if (!maxIncome) return;

      setIncomeSum(maxIncome.income);

      const maxAssets = assets.find((asset) => asset.name === value.name);

      if (!maxAssets) return;

      setAssetSum(maxAssets.assets);
    }
  };

  const handleFormFinish = () => dispatch(proceed(true));

  return (
    <Box sx={{        
      [theme.breakpoints.up("xl")]: {
        marginTop: "10.5em", marginBottom:'7em'
      },
      [theme.breakpoints.up("lg")]: {
        marginTop: "10em",
      },

      [theme.breakpoints.up("xs")]: {
        marginTop: "9em",
      },marginBottom: '8em' }}>
      <Card variant="outlined">
        <CardHeader
       titleTypographyProps={{variant: matches ? 'h3' : 'h4'}}
          title="Eligibility Questionnaire"
          subheader="Please complete the questionnaire below to see if you may be eligible for this program"
          action={<ListAlt />}
        />
        <CardContent>
          <form onSubmit={(event) => event.preventDefault()}>
            <FormControl >
              <FormLabel>Are you a Jewish Nazi victim?</FormLabel>
              <RadioGroup sx={{textAlign: 'center'}}
                row
                name="isSurvivor"
                onChange={handleRadioChange}
                value={isSurvivor}
              >
                <FormControlLabel
                  value={true}
                  control={<Radio />}
                  label="Yes"
                />
                <FormControlLabel
                  value={false}
                  control={<Radio />}
                  label="No"
                />
              </RadioGroup>
            </FormControl>

            <Collapse in={!!isSurvivor}>
              <FormControl>
                <FormLabel>Were you born before February 9th, 1946?</FormLabel>
                <RadioGroup
                  row
                  name="isValidDate"
                  onChange={handleRadioChange}
                  value={isValidDate}
                >
                  <FormControlLabel
                    value={true}
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel
                    value={false}
                    control={<Radio />}
                    label="No"
                  />
                </RadioGroup>
              </FormControl>
            </Collapse>

            <Collapse in={!!isSurvivor && !!isValidDate}>
              <FormControl>
                <FormLabel>
                  Have you ever received Claims Conference compensation?
                </FormLabel>
                <RadioGroup
                  row
                  name="isCompensated"
                  onChange={handleRadioChange}
                  value={isCompensated}
                >
                  <FormControlLabel
                    value={true}
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel
                    value={false}
                    control={<Radio />}
                    label="No"
                  />
                </RadioGroup>
              </FormControl>
            </Collapse>

            <Collapse in={!!isCompensated && !!isSurvivor && !!isValidDate}>
              <FormControl>
                <FormLabel>What country do you reside in?</FormLabel>
                <Autocomplete
                  options={countries}
                  getOptionLabel={(option) => option.name}
                  onChange={handleInputChange}
                  loading={countriesLoading}
                  autoHighlight
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={"Please select your country..."}
                      error={countriesError}
                      helperText={
                        countriesError
                          ? "Failed to load country list. Please refresh the page."
                          : ""
                      }
                    />
                  )}
                  sx={{
                    width: "50%",
                  }}
                />
              </FormControl>
            </Collapse>

            <Collapse
              in={
                !!isEligible && !!isSurvivor && !!isValidDate && !!isCompensated
              }
            >
              <FormControl>
                <FormLabel>
                  Is your annual income below EUR 22,500? Please include all
                  pensions.
                </FormLabel>
                <RadioGroup
                  row
                  name="annualIncome"
                  onChange={handleRadioChange}
                  value={isAboveAnnualIncome}
                >
                  <FormControlLabel
                    value={true}
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel
                    value={false}
                    control={<Radio />}
                    label="No"
                  />
                </RadioGroup>
              </FormControl>
            </Collapse>

            <Collapse
              in={
                !!isEligible &&
                isAboveAnnualIncome === true &&
                !!isSurvivor &&
                !!isCompensated &&
                !!isValidDate
              }
            >
              <FormControl>
                <FormLabel>
                  Are your assets below EUR 22,500? Do not include your primary
                  residence or automobile in this calculation.
                </FormLabel>
                <RadioGroup
                  row
                  name="assets"
                  onChange={handleRadioChange}
                  value={isAboveAssets}
                >
                  <FormControlLabel
                    value={true}
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel
                    value={false}
                    control={<Radio />}
                    label="No"
                  />
                </RadioGroup>
              </FormControl>
            </Collapse>

            <Collapse
              in={
                isAboveAssets === false ||
                isSurvivor === false ||
                isCompensated === false ||
                isEligible === false ||
                isAboveAnnualIncome === false ||
                isValidDate === false
              }
            >
              <Typography variant="h6" sx={{ marginTop: 2 }}>
                Thank you for your interest in the Rest of the World Emergency
                Assistance Program. Unfortunately, based on your responses you
                cannot be eligible for this program. Please direct any questions
                to EAPROW@claimscon.org.
              </Typography>
            </Collapse>

            <Collapse
              in={
                isAboveAnnualIncome === true &&
                isAboveAssets === true &&
                !!isSurvivor &&
                !!isCompensated &&
                !!isEligible &&
                !!isValidDate
              }
            >
              <FormControl>
                <FormLabel>
                  You may be eligible. Please click the button to fill out the
                  application.
                </FormLabel>
                <Button
                  variant="contained"
                  sx={{ marginTop: 2 }}
                  onClick={handleFormFinish}
                >
                  PROCEED
                </Button>
              </FormControl>
            </Collapse>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
